"use client";

import Link from "next/link";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import dynamic from "next/dynamic";
import type { CountryCode } from "libphonenumber-js";
import { COUNTRY_CALLING_CODES, PHONE_COUNTRY_CODES } from "@/lib/phone/country-calling-codes";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { submitContactForm } from "@/app/actions/contact";
// Basari penceresi (Radix Dialog) yalnizca ilk gonderimde yuklenir.
const LeadFormSuccessDialog = dynamic(() => import("./LeadFormSuccessDialog"), { ssr: false });
import Turnstile from "@/components/ui/turnstile";
import { type Locale } from "@/i18n-config";
import { resolveTrackingFields } from "@/lib/attribution";
import { getLocalizedPath } from "@/lib/slugs";

interface FormDictionary {
    form?: {
        name?: string;
        email?: string;
        phone?: string;
        country_search?: string;
        send?: string;
        success?: string;
        error?: string;
        kvkk_consent?: string;
        marketing_consent?: string;
        combined_consent?: string;
        success_title?: string;
        success_description?: string;
        success_button?: string;
    };
}

interface LeadFormProps {
    dict?: FormDictionary;
    submitLabel?: string;
    theme?: "dark" | "light";
    defaultCountry?: CountryCode;
    compact?: boolean;
    idPrefix?: string;
    lang?: Locale;
    // Iki yasal onayi (KVKK + Ticari Elektronik Ileti) tek bir onay kutusunda
    // birlestirir. Isaretlendiginde her ikisi de "on" gonderilir.
    combinedConsent?: boolean;
}

const FALLBACK_TEXT = {
    tr: {
        phoneInvalid: "Geçerli bir telefon numarası girin.",
        countryCode: "Ülke kodu",
        countryNotFound: "Ülke bulunamadı.",
        phoneLabel: "Telefon numarası",
        kvkkLink: "KVKK",
        marketingLink: "Ticari Elektronik İleti İzni",
        genericError: "Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.",
        networkError: "Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.",
        rateLimited: "Çok fazla deneme yapıldı. Lütfen birkaç dakika sonra tekrar deneyin.",
        captchaFailed: "Güvenlik doğrulaması başarısız oldu. Lütfen tekrar deneyin.",
    },
    en: {
        phoneInvalid: "Please enter a valid phone number.",
        countryCode: "Country code",
        countryNotFound: "No country found.",
        phoneLabel: "Phone number",
        kvkkLink: "Privacy Notice",
        marketingLink: "Commercial Communication Consent",
        genericError: "An error occurred while sending the form. Please try again.",
        networkError: "Connection error. Please check your internet connection and try again.",
        rateLimited: "Too many attempts. Please try again in a few minutes.",
        captchaFailed: "Security verification failed. Please try again.",
    },
    ar: {
        phoneInvalid: "يرجى إدخال رقم هاتف صالح.",
        countryCode: "رمز الدولة",
        countryNotFound: "لم يتم العثور على دولة.",
        phoneLabel: "رقم الهاتف",
        kvkkLink: "إشعار الخصوصية",
        marketingLink: "الموافقة على الاتصالات التجارية",
        genericError: "حدث خطأ أثناء إرسال النموذج. يرجى المحاولة مرة أخرى.",
        networkError: "خطأ في الاتصال. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.",
        rateLimited: "محاولات كثيرة جدًا. يرجى المحاولة بعد بضع دقائق.",
        captchaFailed: "فشل التحقق الأمني. يرجى المحاولة مرة أخرى.",
    },
} as const;

const DEFAULT_COUNTRY_BY_LANG: Record<Locale, CountryCode> = {
    tr: "TR",
    en: "GB",
    ar: "SA",
};

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";

// Ulke kodlari sabit tablodan gelir; ~130 KB'lik libphonenumber-js yalnizca
// numara dogrulamasi gerektiginde (alana odaklaninca) yuklenir.
const countryCodes = PHONE_COUNTRY_CODES as unknown as CountryCode[];
const countryCodeSet = new Set<string>(countryCodes);
const dialCodeOf = (code: CountryCode) => `+${COUNTRY_CALLING_CODES[code as keyof typeof COUNTRY_CALLING_CODES] ?? ""}`;
const loadPhoneLib = () => import("libphonenumber-js");

function buildCountries(lang: Locale) {
    let regionNames: Intl.DisplayNames | null = null;
    try {
        regionNames = new Intl.DisplayNames([lang], { type: "region" });
    } catch {
        regionNames = null;
    }

    return countryCodes
        .map((code) => ({
            code,
            name: regionNames?.of(code) || code,
            dialCode: dialCodeOf(code),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, lang));
}

const preferredDialCodeCountries: Partial<Record<string, CountryCode>> = {
    "+1": "US",
    "+7": "RU",
    "+39": "IT",
    "+44": "GB",
    "+47": "NO",
    "+61": "AU",
    "+212": "MA",
    "+262": "RE",
    "+290": "SH",
    "+358": "FI",
    "+590": "GP",
    "+599": "CW",
};

function detectCountryFromBrowser(): CountryCode | null {
    if (typeof window === "undefined") {
        return null;
    }

    const languageCandidates = [
        navigator.language,
        ...(navigator.languages || []),
    ].filter(Boolean);

    for (const locale of languageCandidates) {
        const regionMatch = locale.match(/[-_]([A-Z]{2})$/i);
        if (!regionMatch) {
            continue;
        }

        const country = regionMatch[1].toUpperCase();
        if (countryCodeSet.has(country)) {
            return country as CountryCode;
        }
    }

    try {
        const locale = new Intl.Locale(navigator.language);
        const region = locale.region?.toUpperCase();
        if (region && countryCodeSet.has(region)) {
            return region as CountryCode;
        }
    } catch {
        return null;
    }

    return null;
}

export default function LeadForm({
    dict,
    submitLabel,
    theme = "dark",
    defaultCountry,
    compact = false,
    idPrefix = "lead-form",
    lang = "tr",
    combinedConsent = false,
}: LeadFormProps) {
    const text = FALLBACK_TEXT[lang] || FALLBACK_TEXT.tr;
    const [isPending, startTransition] = useTransition();
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
        defaultCountry || DEFAULT_COUNTRY_BY_LANG[lang] || "GB"
    );
    const [isCountryListOpen, setIsCountryListOpen] = useState(false);
    const [countryQuery, setCountryQuery] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [formError, setFormError] = useState("");
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileKey, setTurnstileKey] = useState(0);
    // Cloudflare Turnstile (challenges.cloudflare.com script'i) sayfa acilisinda
    // degil, ziyaretci forma ilk dokundugunda yuklenir; form doldurulurken
    // dogrulama arka planda tamamlanir.
    const [isEngaged, setIsEngaged] = useState(false);
    const countryPickerRef = useRef<HTMLDivElement>(null);
    const countryFieldId = `${idPrefix}-country`;
    const phoneFieldId = `${idPrefix}-phone`;
    const kvkkPath = getLocalizedPath("kvkk", lang);
    const marketingConsentPath = getLocalizedPath("ticari-elektronik-ileti-izni", lang);
    const countries = useMemo(() => buildCountries(lang), [lang]);

    const styles = useMemo(() => {
        if (theme === "light") {
            return {
                input:
                    `w-full rounded-[6px] border border-slate-200 bg-white px-4 ${compact ? "py-2.5" : "py-3"} text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81]/20 focus:border-[#0f4c81]`,
                select:
                    `w-full rounded-[6px] border border-slate-200 bg-white px-4 ${compact ? "py-2.5" : "py-3"} text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0f4c81]/20 focus:border-[#0f4c81]`,
                checkbox:
                    "border-slate-300 bg-white peer-checked:bg-[#0f4c81] peer-checked:border-[#0f4c81]",
                consent: "text-slate-500",
                button:
                    `w-full rounded-[6px] bg-[#0f4c81] ${compact ? "py-3" : "py-3.5"} text-sm font-semibold text-white transition-colors hover:bg-[#0b3d68]`,
                error: "text-red-600",
            };
        }

        return {
            input:
                `w-full rounded-[4px] border border-transparent bg-[#1e4066] px-4 ${compact ? "py-2.5" : "py-3"} text-sm text-white placeholder:text-blue-100/80 focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`,
            select:
                `w-full rounded-[4px] border border-transparent bg-[#1e4066] px-4 ${compact ? "py-2.5" : "py-3"} text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#00AEEF]`,
            checkbox:
                "border-[#1e4066] bg-[#1e4066] peer-checked:bg-[#00AEEF] peer-checked:border-[#00AEEF]",
            consent: "text-blue-100/70",
            button:
                `w-full rounded-[4px] bg-white ${compact ? "py-3" : "py-3.5"} text-sm font-semibold text-[#0f4c81] transition-colors hover:bg-gray-100`,
            error: "text-red-300",
        };
    }, [compact, theme]);

    useEffect(() => {
        if (defaultCountry) {
            return;
        }

        const detectedCountry = detectCountryFromBrowser();
        if (detectedCountry) {
            const frameId = window.requestAnimationFrame(() => {
                setSelectedCountry(detectedCountry);
            });

            return () => {
                window.cancelAnimationFrame(frameId);
            };
        }
    }, [defaultCountry]);

    useEffect(() => {
        if (!isCountryListOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (!countryPickerRef.current?.contains(event.target as Node)) {
                setIsCountryListOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCountryListOpen]);

    const selectedDialCode = dialCodeOf(selectedCountry);

    const filteredCountries = useMemo(() => {
        const query = countryQuery.trim().toLocaleLowerCase(lang);
        if (!query) {
            return countries;
        }

        const normalizedDialQuery = query.startsWith("+") ? query : `+${query}`;
        const exactDialMatches = countries.filter((country) => country.dialCode === normalizedDialQuery);
        if (exactDialMatches.length > 0) {
            const preferredCountry = preferredDialCodeCountries[normalizedDialQuery];
            if (preferredCountry) {
                return exactDialMatches.filter((country) => country.code === preferredCountry);
            }

            if (exactDialMatches.length === 1) {
                return exactDialMatches;
            }
        }

        return countries.filter((country) =>
            country.name.toLocaleLowerCase(lang).includes(query) ||
            country.code.toLowerCase().includes(query) ||
            country.dialCode.includes(query)
        );
    }, [countries, countryQuery, lang]);

    const validatePhone = async (phoneNumber: string) => {
        const { parsePhoneNumberFromString } = await loadPhoneLib();
        const parsedPhone = parsePhoneNumberFromString(phoneNumber, selectedCountry);
        if (!parsedPhone || !parsedPhone.isValid()) {
            return text.phoneInvalid;
        }

        return "";
    };

    const showError = (message: string) => {
        setFormError(message);
        toast.error(message);
    };

    const resetTurnstile = () => {
        setTurnstileToken("");
        setTurnstileKey((key) => key + 1);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const form = event.currentTarget;
        const formData = new FormData(form);
        const phoneNumber = String(formData.get("phone_number") || "");
        const phoneValidationError = await validatePhone(phoneNumber);

        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            showError(phoneValidationError);
            return;
        }

        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            showError(text.captchaFailed);
            return;
        }

        setPhoneError("");
        setFormError("");
        formData.set("phone_country", selectedCountry);
        formData.set("phone_country_code", selectedDialCode);
        formData.set("cf-turnstile-response", turnstileToken);

        // Kaynak bilgisi gonderim aninda okunur: mevcut URL, saklanan ilk-temas
        // kaydi ve Zoho zcga.js'in gclid cerezi (form sonradan mount edilse de).
        const tracking = resolveTrackingFields();
        for (const [key, value] of Object.entries(tracking)) {
            if (key === "zc_gad" && formData.get("zc_gad")) {
                continue;
            }
            formData.set(key, value);
        }

        startTransition(async () => {
            try {
                const result = await submitContactForm(null, formData);

                if (result.success) {
                    form.reset();
                    setCountryQuery("");
                    setPhoneError("");
                    setFormError("");
                    resetTurnstile();
                    setIsSuccessOpen(true);
                    return;
                }

                resetTurnstile();

                if (result.errors?.phone_number?.[0]) {
                    setPhoneError(text.phoneInvalid);
                }

                if (result.code === "rate_limited") {
                    showError(text.rateLimited);
                } else if (result.code === "captcha_failed") {
                    showError(text.captchaFailed);
                } else {
                    showError(dict?.form?.error || text.genericError);
                }
            } catch (error) {
                console.error("Lead form submit failed:", error);
                resetTurnstile();
                showError(text.networkError);
            }
        });
    };

    return (
        <>
            <form
                className={compact ? "space-y-3" : "space-y-4"}
                onSubmit={handleSubmit}
                onFocusCapture={() => setIsEngaged(true)}
                onPointerDownCapture={() => setIsEngaged(true)}
            >
                {/* Zoho CRM Google Ads alani: zcga.js bu gizli alani name="zc_gad"
                    uzerinden doldurur; bos kalirsa gonderimde gclid cerezinden
                    tamamlanir. Kontrolsuz birakildi ki React degeri ezmesin. */}
                <input type="hidden" id={`${idPrefix}-zc_gad`} name="zc_gad" defaultValue="" />

                <div className={compact ? "grid gap-3 lg:grid-cols-2" : "space-y-4"}>
                    <input
                        name="full_name"
                        type="text"
                        placeholder={dict?.form?.name || "Ad Soyad"}
                        required
                        maxLength={120}
                        autoComplete="name"
                        className={styles.input}
                    />

                    <input
                        name="email"
                        type="email"
                        placeholder={dict?.form?.email || "E-posta Adresi"}
                        required
                        maxLength={160}
                        autoComplete="email"
                        className={styles.input}
                    />
                </div>

                <div className={`grid grid-cols-[112px_minmax(0,1fr)] gap-3 ${compact ? "lg:grid-cols-[170px_minmax(0,1fr)]" : "sm:grid-cols-[148px_minmax(0,1fr)] lg:grid-cols-[200px_minmax(0,1fr)]"}`}>
                    <div className="relative" ref={countryPickerRef}>
                        <label className="sr-only" htmlFor={countryFieldId}>
                            {text.countryCode}
                        </label>
                        <button
                            id={countryFieldId}
                            type="button"
                            onClick={() => {
                                setIsCountryListOpen((open) => {
                                    const nextOpen = !open;
                                    if (!nextOpen) {
                                        setCountryQuery("");
                                    }
                                    return nextOpen;
                                });
                            }}
                            className={`${styles.select} flex items-center justify-between`}
                            aria-haspopup="listbox"
                            aria-expanded={isCountryListOpen}
                        >
                            <span dir="ltr">{selectedDialCode}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${isCountryListOpen ? "rotate-180" : ""}`} />
                        </button>

                        {isCountryListOpen ? (
                            <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                                <div className="sticky top-0 z-10 bg-white p-2">
                                    <input
                                        type="text"
                                        value={countryQuery}
                                        onChange={(event) => setCountryQuery(event.target.value)}
                                        placeholder={dict?.form?.country_search || "Ülke ara"}
                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81]/20 focus:border-[#0f4c81]"
                                    />
                                </div>
                                {filteredCountries.length > 0 ? filteredCountries.map((country) => (
                                    <button
                                        key={country.code}
                                        type="button"
                                        onClick={() => {
                                            setSelectedCountry(country.code);
                                            setCountryQuery("");
                                            setIsCountryListOpen(false);
                                        }}
                                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-start text-sm text-slate-700 transition-colors hover:bg-slate-50"
                                        role="option"
                                        aria-selected={selectedCountry === country.code}
                                    >
                                        <span className="truncate">
                                            {country.name} (<span dir="ltr">{country.dialCode}</span>)
                                        </span>
                                        {selectedCountry === country.code ? (
                                            <Check className="ms-3 h-4 w-4 shrink-0 text-[#0f4c81]" />
                                        ) : null}
                                    </button>
                                )) : (
                                    <div className="px-3 py-4 text-sm text-slate-500">
                                        {text.countryNotFound}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <div>
                        <label className="sr-only" htmlFor={phoneFieldId}>
                            {text.phoneLabel}
                        </label>
                        <input
                            id={phoneFieldId}
                            name="phone_number"
                            type="tel"
                            placeholder={dict?.form?.phone || "Telefon Numarası"}
                            required
                            maxLength={30}
                            autoComplete="tel-national"
                            // Dogrulama kutuphanesini kullanici yazmaya baslamadan once yukle.
                            onFocus={() => void loadPhoneLib()}
                            onChange={(event) => {
                                if (phoneError) {
                                    void validatePhone(event.target.value).then(setPhoneError);
                                }
                            }}
                            className={styles.input}
                        />
                    </div>
                </div>

                {phoneError ? <p className={`text-xs ${styles.error}`}>{phoneError}</p> : null}

                {combinedConsent ? (
                    <div className="pt-2">
                        <label className="group flex cursor-pointer items-start gap-3">
                            <div className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                                <input
                                    name="kvkk_approved"
                                    type="checkbox"
                                    required
                                    className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
                                />
                                <div className={`flex h-full w-full items-center justify-center rounded border-2 transition-all peer-checked:[&_svg]:opacity-100 ${styles.checkbox}`}>
                                    <svg className="h-3.5 w-3.5 text-white opacity-0 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                            <span className={`select-none text-[12px] leading-tight transition-colors ${styles.consent}`}>
                                {dict?.form?.combined_consent ||
                                    "KVKK Aydınlatma Metni ve Ticari Elektronik İleti İzni'ni okudum, onaylıyorum."}
                                {" "}
                                <Link
                                    href={kvkkPath}
                                    target="_blank"
                                    className="font-semibold underline underline-offset-2 transition-colors hover:text-current"
                                >
                                    {text.kvkkLink}
                                </Link>
                                {" · "}
                                <Link
                                    href={marketingConsentPath}
                                    target="_blank"
                                    className="font-semibold underline underline-offset-2 transition-colors hover:text-current"
                                >
                                    {text.marketingLink}
                                </Link>
                            </span>
                        </label>
                        {/* Onay tek kutuda birlestirildi; isaretlendiginde
                            marketing_approved da "on" gonderilir. */}
                        <input type="hidden" name="marketing_approved" value="on" />
                    </div>
                ) : (
                <div className={`${compact ? "grid gap-2 pt-1 lg:grid-cols-2" : "space-y-2 pt-2"}`}>
                    <label className="group flex cursor-pointer items-start gap-3">
                        <div className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                            <input
                                name="kvkk_approved"
                                type="checkbox"
                                required
                                className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
                            />
                            <div className={`flex h-full w-full items-center justify-center rounded border-2 transition-all peer-checked:[&_svg]:opacity-100 ${styles.checkbox}`}>
                                <svg className="h-3.5 w-3.5 text-white opacity-0 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                        <span className={`select-none text-[12px] leading-tight transition-colors ${styles.consent}`}>
                            {dict?.form?.kvkk_consent || "KVKK Aydınlatma Metni'ni okudum ve onaylıyorum"}
                            {" "}
                            <Link
                                href={kvkkPath}
                                target="_blank"
                                className="font-semibold underline underline-offset-2 transition-colors hover:text-current"
                            >
                                {text.kvkkLink}
                            </Link>
                        </span>
                    </label>

                    {/* Ticari elektronik ileti onayi (6563 / IYS) istege baglidir;
                        form gonderimi icin zorunlu tutulmaz. */}
                    <label className="group flex cursor-pointer items-start gap-3">
                        <div className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                            <input
                                name="marketing_approved"
                                type="checkbox"
                                className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
                            />
                            <div className={`flex h-full w-full items-center justify-center rounded border-2 transition-all peer-checked:[&_svg]:opacity-100 ${styles.checkbox}`}>
                                <svg className="h-3.5 w-3.5 text-white opacity-0 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                        <span className={`select-none text-[12px] leading-tight transition-colors ${styles.consent}`}>
                            {dict?.form?.marketing_consent || "Elektronik İleti Onayı'nı okudum ve kabul ediyorum"}
                            {" "}
                            <Link
                                href={marketingConsentPath}
                                target="_blank"
                                className="font-semibold underline underline-offset-2 transition-colors hover:text-current"
                            >
                                {text.marketingLink}
                            </Link>
                        </span>
                    </label>
                </div>
                )}

                {TURNSTILE_SITE_KEY && isEngaged ? (
                    <Turnstile
                        key={turnstileKey}
                        siteKey={TURNSTILE_SITE_KEY}
                        onVerify={setTurnstileToken}
                        onExpire={() => setTurnstileToken("")}
                        onError={() => setTurnstileToken("")}
                    />
                ) : null}

                {formError ? (
                    <p role="alert" className={`text-sm ${styles.error}`}>
                        {formError}
                    </p>
                ) : null}

                <div className={compact ? "pt-1 lg:ms-auto lg:max-w-[180px]" : "pt-2"}>
                    <button
                        type="submit"
                        disabled={isPending}
                        className={`${styles.button} flex items-center justify-center ${isPending ? "cursor-not-allowed opacity-50" : ""}`}
                    >
                        {isPending ? <Loader2 className="me-2 h-5 w-5 animate-spin" /> : null}
                        {submitLabel || dict?.form?.send || "Gönder"}
                    </button>
                </div>
            </form>

            {isSuccessOpen && (
                <LeadFormSuccessDialog
                    open={isSuccessOpen}
                    onOpenChange={setIsSuccessOpen}
                    texts={{
                        title: dict?.form?.success_title || "Mesajınız başarıyla gönderildi",
                        message: dict?.form?.success || "Form başarıyla gönderildi.",
                        description:
                            dict?.form?.success_description ||
                            "Talebiniz ilgili ekibe iletildi. Uygunluk durumuna göre size telefon veya e-posta üzerinden dönüş yapılacaktır.",
                        button: dict?.form?.success_button || "Tamam",
                    }}
                />
            )}
        </>
    );
}
